
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { parseCertificateNumbers } from '@/utils/certificateUtils';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';

interface ImportCertificateNumbersFormData {
  state: string;
  courseType: string;
  certificateNumbers: string;
  notes: string;
}

interface ImportCertificateNumbersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ImportCertificateNumbersDialog({
  open,
  onOpenChange,
  onSuccess
}: ImportCertificateNumbersDialogProps) {
  const { toast } = useToast();
  const [isImporting, setIsImporting] = useState(false);
  
  const form = useForm<ImportCertificateNumbersFormData>({
    defaultValues: {
      state: 'FL',
      courseType: 'BDI',
      certificateNumbers: '',
      notes: '',
    }
  });

  const handleSubmit = async (data: ImportCertificateNumbersFormData) => {
    if (!data.certificateNumbers.trim()) {
      toast({
        title: "No certificate numbers provided",
        description: "Please enter at least one certificate number",
        variant: "destructive"
      });
      return;
    }

    setIsImporting(true);

    try {
      // Parse the certificate numbers
      const parsedNumbers = parseCertificateNumbers(
        data.certificateNumbers,
        data.state,
        data.courseType
      );
      
      if (parsedNumbers.length === 0) {
        toast({
          title: "Invalid input",
          description: "No valid certificate numbers found",
          variant: "destructive"
        });
        setIsImporting(false);
        return;
      }

      // Create a batch record
      const { data: batchData, error: batchError } = await supabase
        .from('certificate_import_batches')
        .insert({
          state: data.state,
          course_type: data.courseType,
          total_imported: parsedNumbers.length,
          notes: data.notes,
          file_name: `manual-import-${new Date().toISOString()}`,
        })
        .select('id')
        .single();

      if (batchError) {
        throw batchError;
      }

      // Insert all certificate numbers into inventory
      const inventoryItems = parsedNumbers.map(item => ({
        certificate_number: item.certificateNumber,
        state: item.state,
        course_type: item.courseType,
        is_used: false,
        import_batch_id: batchData.id
      }));

      const { error: inventoryError } = await supabase
        .from('certificate_number_inventory')
        .insert(inventoryItems);

      if (inventoryError) {
        throw inventoryError;
      }

      toast({
        title: "Import successful",
        description: `Imported ${parsedNumbers.length} certificate numbers for ${data.state} ${data.courseType}`
      });
      
      form.reset();
      onOpenChange(false);
      if (onSuccess) onSuccess();
      
    } catch (error) {
      console.error("Error importing certificate numbers:", error);
      toast({
        title: "Import failed",
        description: error instanceof Error ? error.message : "An error occurred while importing",
        variant: "destructive"
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Import Certificate Numbers</DialogTitle>
          <DialogDescription>
            Add certificate numbers to the inventory. Enter one number per line or separate by commas.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="FL">Florida (FL)</SelectItem>
                        <SelectItem value="CA">California (CA)</SelectItem>
                        <SelectItem value="TX">Texas (TX)</SelectItem>
                        <SelectItem value="NY">New York (NY)</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="courseType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Course Type</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select course type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="BDI">Basic Driver Improvement (BDI)</SelectItem>
                        <SelectItem value="ADI">Advanced Driver Improvement (ADI)</SelectItem>
                        <SelectItem value="DDC">Defensive Driving Course (DDC)</SelectItem>
                        <SelectItem value="TDC">Traffic Driving Course (TDC)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="certificateNumbers"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Certificate Numbers</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field}
                      placeholder="Enter certificate numbers (one per line or comma-separated)"
                      className="min-h-[150px]"
                    />
                  </FormControl>
                  <FormDescription>
                    Example: DC-1234567, DC-1234568, DC-1234569
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field}
                      placeholder="Add any notes about this import batch"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={isImporting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isImporting}>
                {isImporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  'Import Numbers'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
